/**
 * SkillsCatalog — tenant-level inventory of user-authored skills (doc/arch/vertical/SKILLS.md).
 *
 * A tenant skill is a reusable, claude-discoverable asset: a SKILL.md (+ optional
 * MEMORY.md for the self-improving variant) the build agent finds under
 * ``.claude/skills/`` at run time. This panel is the authoring surface — list,
 * create (3 fields + 2 toggles), refine, delete — mirroring SecretsCatalog's
 * table chrome and the workspace-wizard's single-create-event pattern.
 *
 * Scope is TENANT-only in v1 (shown as a fixed chip, never a picker). Create
 * writes a deterministic SKILL.md scaffold server-side and is READY immediately
 * (no async job, no polling) — refinement is the edit path.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Sparkles, RefreshCw, Trash2, Pencil, X as XIcon, Loader2, Brain, GitMerge, AlertTriangle, Check, Wand2 } from 'lucide-react';
import {
  listSkills,
  createSkill,
  getSkillDetail,
  updateSkill,
  deleteSkill,
  refineSkill,
  consolidateSkill,
  releaseConsolidatedSkill,
} from '../../services/toolsApi.js';

function Badge({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    violet: 'bg-violet-50 text-violet-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

export default function SkillsCatalog({ tenantId, registryScope }) {
  // Scope this catalog authors/lists at. Default TENANT (back-compat — the
  // Workspace Settings mount passes nothing). A NETWORK mount (network workbench)
  // passes { kind:'NETWORK', networkId } so skills are authored AND listed at
  // that network (doc/arch/vertical/SKILLS.md). The list is pyramid-aware, so a
  // NETWORK view shows the network's own skills PLUS inherited tenant ones.
  const scope = (registryScope && typeof registryScope === 'object' && registryScope.kind)
    ? registryScope : { kind: 'TENANT' };
  const scopeKind = String(scope.kind || 'TENANT').toUpperCase();
  const scopeNetworkId = String(scope.networkId || '').trim();
  const isNetworkScope = scopeKind === 'NETWORK' && !!scopeNetworkId;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null); // skillId being refined, or null
  const [consolidating, setConsolidating] = useState(null); // {skillId, name} or null

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // NETWORK scope: pass networkId → pyramid returns network + tenant rows.
      // TENANT scope: no filter → tenant-wide rows.
      const res = await listSkills(isNetworkScope ? { networkId: scopeNetworkId } : {});
      setRows(Array.isArray(res?.secrets) ? res.secrets : []);
    } catch (e) {
      setError(e?.message || 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, [isNetworkScope, scopeNetworkId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onCreated = useCallback(() => {
    setWizardOpen(false);
    refresh();
  }, [refresh]);

  const onEdited = useCallback(() => {
    setEditing(null);
    refresh();
  }, [refresh]);

  const handleDelete = useCallback(async (skillId, name) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete skill "${name}"? This removes its SKILL.md and any experience log. Agents still pinned to it will block the delete.`)) {
      return;
    }
    try {
      await deleteSkill(skillId);
      refresh();
    } catch (e) {
      // 409 = still in use; surface the message.
      setError(e?.message || 'Delete failed');
    }
  }, [refresh]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <Sparkles className="h-4 w-4 text-violet-600" aria-hidden />
            Skills
          </h2>
          <p className="mt-0.5 text-[11px] text-gray-500">
            Reusable instructions your builders discover automatically. Author once, attach to any workspace.{' '}
            <Badge tone="gray">{isNetworkScope ? 'Network-scoped' : 'Tenant-scoped'}</Badge>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            data-testid="skills-refresh"
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            data-testid="skills-new"
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New skill
          </button>
        </div>
      </div>

      {error ? (
        <div className="shrink-0 border-b border-red-100 bg-red-50 px-4 py-2 text-[11px] text-red-700">{error}</div>
      ) : null}

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Name</th>
              <th className="px-4 py-1.5 text-left font-medium">Type</th>
              <th className="px-4 py-1.5 text-left font-medium">Version</th>
              <th className="px-4 py-1.5 text-left font-medium">Updated</th>
              <th className="px-4 py-1.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                  No skills yet. Create one to encode a workflow your builders can reuse.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <SkillRow
                  key={r.secretId}
                  row={r}
                  onEdit={() => setEditing(r.secretId)}
                  onDelete={() => handleDelete(r.secretId, r.secretName)}
                  onConsolidate={() => setConsolidating({ skillId: r.secretId, name: r.secretName })}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {wizardOpen ? (
        <SkillEditorDialog mode="create" scope={scope} onClose={() => setWizardOpen(false)} onSaved={onCreated} />
      ) : null}
      {editing ? (
        <SkillEditorDialog mode="edit" skillId={editing} onClose={() => setEditing(null)} onSaved={onEdited} />
      ) : null}
      {consolidating ? (
        <ConsolidateDialog
          skillId={consolidating.skillId}
          name={consolidating.name}
          onClose={() => setConsolidating(null)}
          onReleased={() => { setConsolidating(null); refresh(); }}
        />
      ) : null}
    </div>
  );
}

function SkillRow({ row, onEdit, onDelete, onConsolidate }) {
  // The list (secrets-registry slim row) doesn't carry config; the
  // self-improving flag + description live on the detail. Show what we have and
  // let the editor reveal the rest.
  const updated = row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—';
  return (
    <tr className="hover:bg-gray-50/60">
      <td className="px-4 py-2">
        <span className="font-medium text-gray-900">{row.secretName}</span>
      </td>
      <td className="px-4 py-2">
        {row.selfImproving ? (
          <Badge tone="violet"><Brain className="h-3 w-3" aria-hidden /> Self-improving</Badge>
        ) : (
          <Badge tone="gray">Static</Badge>
        )}
      </td>
      <td className="px-4 py-2 text-gray-500">v{String(row.version ?? 1)}</td>
      <td className="px-4 py-2 text-gray-500">{updated}</td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1.5">
          {row.selfImproving ? (
            <button
              type="button"
              onClick={onConsolidate}
              data-testid={`skills-consolidate-${row.secretId}`}
              title="Merge what every deployed builder has learned into one versioned release"
              className="inline-flex items-center gap-1 rounded-md border border-violet-200 px-2 py-1 text-[11px] font-medium text-violet-700 hover:bg-violet-50"
            >
              <GitMerge className="h-3 w-3" aria-hidden /> Consolidate
            </button>
          ) : null}
          <button
            type="button"
            onClick={onEdit}
            data-testid={`skills-edit-${row.secretId}`}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
          >
            <Pencil className="h-3 w-3" aria-hidden /> Refine
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`skills-delete-${row.secretId}`}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3 w-3" aria-hidden /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * SkillEditorDialog — create OR refine. The 3-field intake (name / when-to-use /
 * what-it-does) + the self-improving toggle. Single save event (no Done screen).
 * In edit mode it loads the existing detail first.
 */
function SkillEditorDialog({ mode, skillId, scope, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [selfImproving, setSelfImproving] = useState(false);
  const [memory, setMemory] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // "✨ Improve with AI" — opt-in. ``suggestion`` holds the model's proposal;
  // the user accepts (Use this) or dismisses (Keep mine). Never auto-applied.
  const [refining, setRefining] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { description, instructions, why }

  const handleRefine = useCallback(async () => {
    if (refining || (!description.trim() && !instructions.trim())) return;
    setRefining(true);
    setError('');
    try {
      const res = await refineSkill({
        name: name.trim(), description: description.trim(), instructions: instructions.trim(),
      });
      if (res?.improved && res.suggestion) setSuggestion(res.suggestion);
      else setError('No improvement suggested — your version looks good.');
    } catch (e) {
      setError(e?.message || 'Improve failed');
    } finally {
      setRefining(false);
    }
  }, [refining, name, description, instructions]);

  const acceptSuggestion = useCallback(() => {
    if (!suggestion) return;
    if (suggestion.description) setDescription(suggestion.description);
    if (suggestion.instructions) setInstructions(suggestion.instructions);
    setSuggestion(null);
  }, [suggestion]);

  useEffect(() => {
    if (!isEdit || !skillId) return undefined;
    let alive = true;
    (async () => {
      try {
        const d = await getSkillDetail(skillId);
        if (!alive) return;
        setName(d?.name || '');
        setDescription(d?.description || '');
        setSelfImproving(Boolean(d?.selfImproving));
        setMemory(d?.memory || '');
        // The detail returns the FULL SKILL.md body. Strip frontmatter + the
        // title heading + the self-improving contract so the textarea shows
        // just the user's instructions (the same text re-render expects).
        setInstructions(extractInstructions(d?.body || ''));
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load skill');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isEdit, skillId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSave = !busy && name.trim() && description.trim() && instructions.trim();

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setBusy(true);
    setError('');
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        instructions: instructions.trim(),
        selfImproving,
      };
      if (isEdit) {
        await updateSkill(skillId, body);
      } else {
        // Author at the catalog's scope (TENANT default; NETWORK from the
        // network-workbench mount). The backend re-validates membership.
        const createScope = (scope && typeof scope === 'object' && scope.kind)
          ? scope : { kind: 'TENANT' };
        await createSkill({ ...body, scope: createScope });
      }
      onSaved();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }, [canSave, name, description, instructions, selfImproving, isEdit, skillId, scope, onSaved]);

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="skill-editor-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-1.5 text-base font-semibold text-gray-900">
              <Sparkles className="h-4 w-4 text-violet-600" aria-hidden />
              {isEdit ? 'Refine skill' : 'New skill'}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {isEdit
                ? 'Edit the intake and re-render. Every save bumps the version.'
                : 'Describe a workflow your builders should reuse. Three fields — the rest is scaffolded.'}
              {' '}<Badge tone="gray">
                {String(scope?.kind || 'TENANT').toUpperCase() === 'NETWORK' ? 'Network-scoped' : 'Tenant-scoped'}
              </Badge>
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
            </div>
          ) : (
            <div className="space-y-4">
              <Field label="Name" hint="A short, memorable title.">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  placeholder="PR review checklist"
                  data-testid="skill-name"
                  disabled={isEdit /* renaming = new skill; keep id/dir stable */}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
                {isEdit ? <p className="mt-1 text-[11px] text-gray-400">Name is fixed after creation — create a new skill to rename.</p> : null}
              </Field>

              {/* ✨ Improve with AI — opt-in. Sharpens the trigger + instructions;
                  shown as accept/reject so the user stays in control. */}
              <div className="flex items-center justify-between rounded-lg border border-dashed border-violet-200 bg-violet-50/40 px-3 py-2">
                <span className="text-[11px] text-violet-800">
                  Not sure your wording is sharp? Let AI suggest improvements — you choose whether to use them.
                </span>
                <button
                  type="button"
                  onClick={handleRefine}
                  disabled={refining || (!description.trim() && !instructions.trim())}
                  data-testid="skill-refine"
                  className="ml-3 inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {refining ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Wand2 className="h-3.5 w-3.5" aria-hidden />}
                  {refining ? 'Improving…' : 'Improve with AI'}
                </button>
              </div>

              {/* Suggestion — accept/reject, never auto-applied. */}
              {suggestion ? (
                <div className="rounded-lg border border-violet-300 bg-white p-3 shadow-sm" data-testid="skill-suggestion">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-800">
                    <Wand2 className="h-3.5 w-3.5" aria-hidden /> Suggested improvement
                  </div>
                  {suggestion.why ? <p className="mt-1 text-[11px] italic text-gray-500">{suggestion.why}</p> : null}
                  {suggestion.description ? (
                    <div className="mt-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">When to use it</p>
                      <p className="mt-0.5 rounded bg-violet-50 px-2 py-1 text-[12px] text-gray-800">{suggestion.description}</p>
                    </div>
                  ) : null}
                  {suggestion.instructions ? (
                    <div className="mt-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">What it should do</p>
                      <pre className="mt-0.5 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-violet-50 px-2 py-1 font-mono text-[11px] text-gray-800">{suggestion.instructions}</pre>
                    </div>
                  ) : null}
                  <div className="mt-2.5 flex items-center justify-end gap-2">
                    <button type="button" onClick={() => setSuggestion(null)}
                      className="rounded-md border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50">
                      Keep mine
                    </button>
                    <button type="button" onClick={acceptSuggestion} data-testid="skill-suggestion-accept"
                      className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-violet-700">
                      <Check className="h-3 w-3" aria-hidden /> Use this
                    </button>
                  </div>
                </div>
              ) : null}

              <Field
                label="When to use it"
                emphasis
                hint="The most important field — the model reads this to decide when to invoke the skill. Name the situation, not just the topic — specific triggers fire reliably; vague ones fire wrongly or never."
              >
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                  rows={4}
                  placeholder="Use when reviewing a pull request for our backend services. Checks error handling, logging, and our API conventions."
                  data-testid="skill-description"
                  className="w-full rounded-lg border border-violet-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
                <p className="mt-1 text-right text-[10px] text-gray-400">{description.length}/2000</p>
              </Field>

              <Field label="What it should do" hint="Plain-English steps, rules, or a checklist the builder follows once invoked.">
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={8}
                  placeholder={'1. Read the diff and the linked issue.\n2. Verify error paths are handled and logged.\n3. Check naming matches our conventions in docs/STYLE.md.\n4. Summarize findings as a checklist comment.'}
                  data-testid="skill-instructions"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-[13px] leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </Field>

              <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selfImproving}
                  onChange={(e) => setSelfImproving(e.target.checked)}
                  data-testid="skill-self-improving"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-xs">
                  <span className="flex items-center gap-1 font-medium text-gray-800">
                    <Brain className="h-3.5 w-3.5 text-violet-600" aria-hidden /> Self-improving
                  </span>
                  <span className="mt-0.5 block text-gray-500">
                    Keeps an experience log (<span className="font-mono">MEMORY.md</span>) — learns from corrected runs.
                    Lessons are appended only on a real signal, reviewable here, and git-revertible.
                  </span>
                </span>
              </label>

              {isEdit && selfImproving && memory ? (
                <Field label="What this skill has learned" hint="Read-only — appended at run time from corrected runs.">
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">{memory}</pre>
                </Field>
              ) : null}

              {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            data-testid="skill-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {busy ? 'Saving…' : isEdit ? 'Save & re-render' : 'Create skill'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ConsolidateDialog — fan-in review. Loads the consolidation preview (consensus
 * + contradictions), lets the admin pick a winning fix for each contradiction
 * (NEVER auto-resolved), then releases the merged KB as a new version.
 *
 * The algorithm proposes; the human disposes. Consensus is shown read-only;
 * contradictions REQUIRE a choice before release is enabled.
 */
function ConsolidateDialog({ skillId, name, onClose, onReleased }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { consensus, contradictions, sourceCount }
  const [choices, setChoices] = useState({});   // trigger -> chosen fix index
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await consolidateSkill(skillId);
        if (!alive) return;
        setPreview(p);
      } catch (e) {
        if (alive) setError(e?.message || 'Consolidation preview failed');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [skillId]);

  const contradictions = preview?.contradictions || [];
  const allResolved = contradictions.every((c) => choices[c.trigger] != null);

  const handleRelease = useCallback(async () => {
    if (!allResolved || busy) return;
    setBusy(true);
    setError('');
    try {
      // Assemble the approved set: every consensus lesson + the admin's chosen
      // fix per contradiction.
      const lessons = [
        ...(preview.consensus || []).map((l) => ({
          trigger: l.trigger, fix: l.fix, signal: l.signal, count: l.count,
        })),
        ...contradictions.map((c) => {
          const opt = c.options[choices[c.trigger]];
          return { trigger: c.trigger, fix: opt.fix, signal: 'human', count: opt.count };
        }),
      ];
      await releaseConsolidatedSkill(skillId, lessons);
      onReleased();
    } catch (e) {
      setError(e?.message || 'Release failed');
    } finally {
      setBusy(false);
    }
  }, [allResolved, busy, preview, contradictions, choices, skillId, onReleased]);

  const sourceCount = preview?.sourceCount || 0;
  const nothingToDo = !loading && sourceCount <= 1;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" data-testid="skill-consolidate-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-1.5 text-base font-semibold text-gray-900">
              <GitMerge className="h-4 w-4 text-violet-600" aria-hidden /> Consolidate “{name}”
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Merge what every deployed builder has independently learned into one
              versioned release. Agreements are auto-promoted; disagreements are yours to resolve.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Scanning deployed copies…
            </div>
          ) : nothingToDo ? (
            <div className="py-10 text-center">
              <p className="text-sm text-gray-600">Nothing to consolidate yet.</p>
              <p className="mx-auto mt-1 max-w-sm text-[11px] text-gray-400">
                Consolidation merges what your deployed builders have independently learned.
                Attach this skill to workspaces and run it across them first — once their
                experience logs diverge, come back here to merge and release a new version.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-[11px] text-gray-500">
                Merged across <span className="font-medium text-gray-700">{sourceCount}</span> source
                {sourceCount === 1 ? '' : 's'} (master + deployed workspaces).
              </p>

              {/* Contradictions — REQUIRE a choice */}
              {contradictions.length > 0 ? (
                <div>
                  <h3 className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                    Disagreements — pick the rule to keep ({contradictions.length})
                  </h3>
                  <div className="mt-2 space-y-3">
                    {contradictions.map((c) => (
                      <div key={c.trigger} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                        <p className="text-[11px] font-medium text-gray-800">WHEN {c.trigger}</p>
                        <div className="mt-1.5 space-y-1.5">
                          {c.options.map((opt, i) => (
                            <label key={i} className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-2 py-1 hover:border-amber-200 hover:bg-white">
                              <input
                                type="radio"
                                name={`contra-${c.trigger}`}
                                checked={choices[c.trigger] === i}
                                onChange={() => setChoices((prev) => ({ ...prev, [c.trigger]: i }))}
                                data-testid={`consolidate-choice-${c.trigger}-${i}`}
                                className="mt-0.5 h-3.5 w-3.5 text-violet-600 focus:ring-violet-500"
                              />
                              <span className="text-[11px]">
                                <span className="font-medium text-gray-800">DO {opt.fix}</span>
                                <span className="ml-1 text-gray-400">
                                  · {opt.sources.length} source{opt.sources.length === 1 ? '' : 's'} · ×{opt.count}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Consensus — auto-promoted, read-only */}
              {(preview?.consensus || []).length > 0 ? (
                <div>
                  <h3 className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
                    <Check className="h-3.5 w-3.5" aria-hidden />
                    Agreed — auto-promoted ({preview.consensus.length})
                  </h3>
                  <ul className="mt-2 space-y-1">
                    {preview.consensus.map((l, i) => (
                      <li key={i} className="rounded-md bg-gray-50 px-2.5 py-1.5 text-[11px] text-gray-700">
                        <span className="text-gray-500">WHEN</span> {l.trigger}{' '}
                        <span className="text-gray-500">→ DO</span> {l.fix}
                        <span className="ml-1 text-gray-400">· ×{l.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRelease}
            disabled={busy || loading || nothingToDo || !allResolved}
            data-testid="consolidate-release"
            title={!allResolved ? 'Resolve every disagreement first' : ''}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <GitMerge className="h-3.5 w-3.5" aria-hidden />}
            {busy ? 'Releasing…' : 'Release new version'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, emphasis, children }) {
  return (
    <div>
      <label className={`block text-sm font-medium ${emphasis ? 'text-violet-800' : 'text-gray-700'}`}>{label}</label>
      {hint ? <p className="mb-1 mt-0.5 text-[11px] text-gray-500">{hint}</p> : null}
      {children}
    </div>
  );
}

/** Strip YAML frontmatter, the leading ``# title`` heading, and any trailing
 *  self-improving contract block from a SKILL.md so the textarea shows just the
 *  authored instructions (the inverse of the server's render). Mirrors
 *  ``skills_registry_impl._existing_instructions`` so a refine round-trips. */
export function extractInstructions(skillMd) {
  let text = String(skillMd || '');
  if (text.startsWith('---')) {
    const end = text.indexOf('\n---', 3);
    if (end !== -1) {
      const nl = text.indexOf('\n', end + 1);
      text = nl !== -1 ? text.slice(nl + 1) : '';
    }
  }
  text = text.replace(/^\n+/, '');
  if (text.startsWith('# ')) {
    const nl = text.indexOf('\n');
    text = nl !== -1 ? text.slice(nl + 1) : '';
  }
  const marker = '## Experience (self-improving)';
  const idx = text.indexOf(marker);
  if (idx !== -1) text = text.slice(0, idx);
  return text.trim();
}
